import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const add_station_type_image =
  process.env.PUBLIC_URL + "/image/misc/add_station_type.svg";

const cobot_image = process.env.PUBLIC_URL + "/image/icons/cobot_icon.webp";
const conveyor_image =
  process.env.PUBLIC_URL + "/image/icons/conveyor_station.webp";
const static_image =
  process.env.PUBLIC_URL + "/image/icons/static_station.webp";
const inspectron_image =
  process.env.PUBLIC_URL + "/image/icons/inspectron.webp";
const mobile_image =
  process.env.PUBLIC_URL + "/image/icons/mobile_station.webp";
const cctv_image = process.env.PUBLIC_URL + "/image/icons/cctv.webp";

// ACTUAL VALUES TO SEND TO API (Never change these)
export const ACTUAL_COMMUNICATION_PROTOCOLS = [
  "Modbus TCP",
  "Modbus RTU",
  "Profinet",
  "Socket / TCP",
  "Serial Communication",
];

export const ACTUAL_CAMERA_TYPES = [
  "IP Cam",
  "USB Cam",
  "GIG-E (Baumer)",
  "RTSP",
  "File System",
];

export const ACTUAL_PERIPHERAL_DEVICE_TYPES = [
  "Keyence",
  "Cognex",
];

export const ACTUAL_CAMERA_DIRECTIONS = [
  "Right to Left",
  "Left to Right",
];

export const ACTUAL_CAMERA_CONTROLS = [
  "Absolute",
  "Increment",
];

export const plc_values = {
  plc_name: "",
  communication_protocol: "",
  interface_address: "",
};

export const camera_values = {
  camera_name: "",
  camera_type: "",
  camera_address: "",
};

export const peripheral_device_values = {
  device_name: "",
  device_type: "",
  device_address: "",
};

export const useStationSteps = () => {
  const { t } = useTranslation();
  const station_type = [
    {
      id: "static-station",
      type: "static",
      label: t("Static"),
      image: static_image,
    },
    {
      id: "conveyor",
      type: "conveyor",
      label: t("Conveyor"),
      image: conveyor_image,
    },
    {
      id: "cctv",
      type: "cctv",
      label: t("CCTV"),
      image: cctv_image,
    },
    {
      id: "inspectron",
      type: "inspectron",
      label: t("WiTorch®"),
      image: inspectron_image,
    },
    {
      id: "mobile",
      type: "mobile",
      label: t("Mobile"),
      image: mobile_image,
    },
  ];
  return station_type;
};

export const station_type = [
  {
    id: "static-station",
    type: "static",
    label: "Static Station",
    image: static_image,
  },
  {
    id: "conveyor",
    type: "conveyor",
    label: "Conveyor",
    image: conveyor_image,
  },
  {
    id: "cctv",
    type: "cctv",
    label: "CCTV",
    image: cctv_image,
  },
  {
    id: "inspectron",
    type: "inspectron",
    label: "WiTorch®",
    image: inspectron_image,
  },
  {
    id: "mobile",
    type: "mobile",
    label: "Mobile",
    image: mobile_image,
  },
  {
    id: "co-bot",
    type: "cobot",
    label: "Cobot",
    image: cobot_image,
  },
];

// Hook to get translated communication protocols with mapping to actual values
export const useCommunicationProtocol = () => {
  const { t } = useTranslation();
  
  const protocolMapping = {
    "Modbus TCP": t("Modbus TCP"),
    "Modbus RTU": t("Modbus RTU"),
    "Profinet": t("Profinet"),
    "Socket / TCP": t("Socket / TCP"),
    "Serial Communication": t("Serial Communication"),
  };
  
  return {
    display: Object.values(protocolMapping), // For displaying in UI
    mapping: protocolMapping, // For converting display to actual value
    actual: ACTUAL_COMMUNICATION_PROTOCOLS, // Actual values
  };
};

export const communication_protocol = ACTUAL_COMMUNICATION_PROTOCOLS;

// Hook to get translated camera types with mapping
export const useCameraType = () => {
  const { t } = useTranslation();
  
  const cameraTypeMapping = {
    "IP Cam": t("IP Cam"),
    "USB Cam": t("USB Cam"),
    "GIG-E (Baumer)": t("GIG-E (Baumer)"),
    "RTSP": t("RTSP"),
    "File System": t("File System"),
  };
  
  return {
    display: Object.values(cameraTypeMapping), // For displaying in UI
    mapping: cameraTypeMapping, // For converting display to actual value
    actual: ACTUAL_CAMERA_TYPES, // Actual values
  };
};

export const camera_type = ACTUAL_CAMERA_TYPES;

// Hook to get translated peripheral device types with mapping
export const usePeripheralDeviceType = () => {
  const { t } = useTranslation();
  
  const deviceTypeMapping = {
    "Keyence": t("Keyence"),
    "Cognex": t("Cognex"),
  };
  
  return {
    display: Object.values(deviceTypeMapping), // For displaying in UI
    mapping: deviceTypeMapping, // For converting display to actual value
    actual: ACTUAL_PERIPHERAL_DEVICE_TYPES, // Actual values
  };
};

export const peripheral_device_type = ACTUAL_PERIPHERAL_DEVICE_TYPES;

// Hook to get translated camera directions with mapping
export const useCameraDirection = () => {
  const { t } = useTranslation();
  
  const directionMapping = {
    "Right to Left": t("Right to Left"),
    "Left to Right": t("Left to Right"),
  };
  
  return {
    display: Object.values(directionMapping), // For displaying in UI
    mapping: directionMapping, // For converting display to actual value
    actual: ACTUAL_CAMERA_DIRECTIONS, // Actual values
  };
};

export const camera_direction = ACTUAL_CAMERA_DIRECTIONS;

// Hook to get translated camera controls with mapping
export const useCameraControl = () => {
  const { t } = useTranslation();
  
  const controlMapping = {
    "Absolute": t("Absolute"),
    "Increment": t("Increment"),
  };
  
  return {
    display: Object.values(controlMapping), // For displaying in UI
    mapping: controlMapping, // For converting display to actual value
    actual: ACTUAL_CAMERA_CONTROLS, // Actual values
  };
};

export const camera_control = ACTUAL_CAMERA_CONTROLS;

export const defaultFormValues = {
  workstation_name: "",
  workstation_plant_name: "",
  workstation_location: "",
  workstation_status: true,
  workstation_type: "",
  workstation_type_detail: {},
  plc: [plc_values],
  camera: [camera_values],
  peripheral_device: [peripheral_device_values],
};

export const useStationForm = () => {
  return useForm({
    mode: "all",
    defaultValues: defaultFormValues,
  });
};

// Utility function to convert translated value back to actual value
export const getActualValue = (displayValue: string, mapping: Record<string, string>): string => {
  // Find the actual value by matching the display value
  const actualValue = Object.entries(mapping).find(
    ([_, displayVal]) => displayVal === displayValue
  )?.[0];
  return actualValue || displayValue; // Return actual value or display value as fallback
};